import type { AttachmentType } from '@/features/messages';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getConversationAttachments } from '../services/conversation.service';
import { conversationKeys } from './conversation-keys';

interface UseAttachmentsParams {
  conversationId?: Nullable<string>;
  contentType?: AttachmentType;
  limit?: number;
  enabled?: boolean;
}

type UseAttachmentsArg = string | UseAttachmentsParams;

export const useAttachments = (arg: UseAttachmentsArg) => {
  const normalized: UseAttachmentsParams = typeof arg === 'string' ? { conversationId: arg } : arg;
  const { conversationId, contentType, limit = 20, enabled = true } = normalized;
  const query = useInfiniteQuery({
    queryKey: conversationKeys.attachments(conversationId || '', contentType, limit),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getConversationAttachments({
        conversationId: conversationId!,
        contentType,
        limit,
        pageToken: pageParam
      }),
    enabled: !!conversationId && enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false
  });

  const flatItems = query.data?.pages.flatMap((p) => p.attachments) ?? [];

  return {
    files: flatItems,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error
  };
};
